/**
 * Custom navigation UI component for Gonvarri storefront
 * Extends Spartacus navigation with additional features for contract-based users
 * and improved accessibility and positioning of dropdown menus
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, FeatureConfigService, WindowRef } from '@spartacus/core';
import { Observable, of, Subject, Subscription, fromEvent } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
} from 'rxjs/operators';
import { 
  BREAKPOINT, 
  BreakpointService, 
  HamburgerMenuService, 
  ICON_TYPE, 
  NavigationNode 
} from "@spartacus/storefront";
import { GonvarriClientProgramAdapter } from "../../core/adapters/gonvarri-client-program.adapter";

const ARIA_EXPANDED_ATTR = 'aria-expanded';

@Component({
    selector: 'cx-navigation-ui',
    templateUrl: './gonvarri-navigation-ui.component.html',
    styleUrl: './gonvarri-navigation-ui.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GonvarriNavigationUIComponent implements OnInit, OnDestroy {
    
  /**
   * Tracks user login status and checks for contract access
   */
  isLoggedIn: any = this.authService.isUserLoggedIn().subscribe(logged => {
    this.adapter.getUserHasContract().subscribe(hasContract => {
      this.hasContract$ = of(hasContract.hasContract ? "yes" : "no");
    });
  });

  /** Contract status observable for template binding */
  hasContract$: Observable<string> = of("no");
  
  /** Main navigation node for this component */
  @Input() node: NavigationNode | null = null;

  /** Number of items before wrapping to new column */
  @Input() wrapAfter: number = 0;

  /** Whether to reset menu when closed */
  @Input() resetMenuOnClose: boolean | undefined;

  /** Accessibility label for navigation */
  @Input() navAriaLabel: string | null | undefined;

  /** Icon types from Spartacus */
  iconType = ICON_TYPE;

  /** Controls flyout behavior */
  @Input() @HostBinding('class.flyout') flyout = true;

  /** Controls open state class binding */
  @Input() @HostBinding('class.is-open') isOpen = false;
  
  
  /** Tracks currently open navigation nodes */
  private openNodes: HTMLElement[] = [];
  
  /** Manages all component subscriptions */
  private subscriptions = new Subscription();
  
  /** Emitter for resize events */
  private resize = new EventEmitter();
  
  /** Subject for keyboard arrow navigation */
  protected arrowControls: Subject<KeyboardEvent> = new Subject();
  
  /** Tracks mouseenter subscription */
  private mouseEnterSubscription: Subscription | null = null;
  
  /** Responsive design service */
  protected breakpointService = inject(BreakpointService);
  
  /** Tracks desktop viewport state */
  isDesktop$ = this.breakpointService.isUp(BREAKPOINT.lg);
  
  /**
   * Handles window resize events
   */
  @HostListener('window:resize')
  onResize() {
    this.resize.next(undefined);
  }

  /**
   * Handles keyboard arrow navigation
   */
  @HostListener('document:keyDown.arrowUp', ['$event'])
  @HostListener('document:keyDown.arrowDown', ['$event'])
  onArrow(e: KeyboardEvent) {
    this.arrowControls.next(e);
  }

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private elemRef: ElementRef,
    protected hamburgerMenuService: HamburgerMenuService,
    protected winRef: WindowRef,
    protected adapter: GonvarriClientProgramAdapter,
    private authService: AuthService,
    @Optional() protected featureConfigService?: FeatureConfigService
  ) {
    // Clear navigation on route change
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.clear())
    );
    
    // Handle resize events
    this.subscriptions.add(
      this.resize.pipe(debounceTime(50)).subscribe(() => {
        this.alignWrappersToRightIfStickOut();
      })
    );
  }
  
  /**
   * Initialize component, set up event listeners and align dropdowns
   */
  ngOnInit() {
    if (this.resetMenuOnClose) {
      this.resetOnMenuCollapse();
    }
    
    // Add window resize listener to adjust dropdowns
    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(() => {
          if (this.isOpen) {
            this.alignWrappersToRightIfStickOut();
          }
        })
    );

    // Add hover event handlers for dropdown positioning
    this.addHoverEventListeners();

    // Ensure initial alignment of all dropdowns
    this.alignWrappersToRightIfStickOut();
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    if (this.mouseEnterSubscription) {
      this.mouseEnterSubscription.unsubscribe();
    }
  }
  
  /**
   * Subscribes to hamburger menu state to reset navigation when menu collapses
   */
  resetOnMenuCollapse(): void {
    this.subscriptions.add(
      this.hamburgerMenuService?.isExpanded
        .pipe(distinctUntilChanged(), filter(Boolean))
        .subscribe(() => {
          this.reinitializeMenu();
        })
    );
  }

  /**
   * Handles clicks on links that match current URL
   * Closes the menu when clicking the same link
   */
  closeIfClickedTheSameLink(navNode: NavigationNode): void {
    if (
      typeof navNode.url === 'string' &&
      this.winRef.nativeWindow?.location.href.includes(navNode.url)
    ) {
      if (
        this.featureConfigService?.isEnabled('a11yNavigationUiKeyboardControls')
      ) {
        this.reinitializeMenu();
      } else {
        this.elemRef.nativeElement
          .querySelectorAll('li.is-open:not(.back), li.is-opened')
          .forEach((el: any) => {
            this.renderer.removeClass(el, 'is-open');
            this.renderer.removeClass(el, 'is-opened');
          });
        this.reinitializeMenu();
      }
      this.hamburgerMenuService.toggle();
    }
  }

  /**
   * Resets the menu to initial state, accounting for accessibility settings
   */
  reinitializeMenu(): void {
    const a11yKeyboardControlsEnabled = this.featureConfigService?.isEnabled(
      'a11yNavigationUiKeyboardControls'
    );
    const a11yNavMenuExpandStateReadout = this.featureConfigService?.isEnabled(
      'a11yNavMenuExpandStateReadout'
    );
    
    if (a11yKeyboardControlsEnabled || a11yNavMenuExpandStateReadout) {
      const listItems = this.elemRef.nativeElement.querySelectorAll(
        'li.is-open:not(.back), li.is-opened'
      );

      if (a11yNavMenuExpandStateReadout) {
        listItems.forEach((el: HTMLElement) => {
          Array.from(el.children)
            .filter((childNode) => childNode?.tagName === 'BUTTON')
            .forEach((childNode) => {
              this.renderer.setAttribute(
                childNode,
                ARIA_EXPANDED_ATTR,
                'false'
              );
            });
        });
      }
      
      if (a11yKeyboardControlsEnabled) {
        listItems.forEach((el: HTMLElement) => {
          this.renderer.removeClass(el, 'is-open');
          this.renderer.removeClass(el, 'is-opened');
        });
        this.clear();
        this.renderer.removeClass(this.elemRef.nativeElement, 'is-open');
        this.updateClasses();
      }
    } else if (this.openNodes?.length > 0) {
      this.renderer.removeClass(this.elemRef.nativeElement, 'is-open');
      this.clear();
    }
  }

  /**
   * Updates ARIA attributes when collapsing nodes
   */
  protected ariaCollapseNodes(): void {
    this.openNodes.forEach((parentNode) => {
      Array.from(parentNode.children)
        .filter((childNode) => childNode?.tagName === 'BUTTON')
        .forEach((childNode) => {
          this.renderer.setAttribute(childNode, ARIA_EXPANDED_ATTR, 'false');
        });
    });
  }

  /**
   * Toggles the open/closed state of navigation nodes
   * Handles menu navigation by updating the active nodes list
   * @param event The UI event that triggered the toggle
   */
  toggleOpen(event: UIEvent): void {
    if (event.type === 'keydown') {
      event.preventDefault();
    }
    
    // Reset aria-expanded state for all currently open nodes
    this.ariaCollapseNodes();
    
    // Get the current node and its parent
    const node = <HTMLElement>event.currentTarget;
    const parentNode = <HTMLElement>node.parentNode;
    
    // Toggle node open/closed state
    if (this.openNodes.includes(parentNode)) {
      if (event.type === 'keydown') {
        // Handle keyboard navigation differently
        this.back();
      } else {
        // Remove node from open nodes list
        this.openNodes = this.openNodes.filter((n) => n !== parentNode);
        this.renderer.removeClass(parentNode, 'is-open');
      }
    } else {
      // Add node to open nodes list
      this.openNodes.push(parentNode);
      this.renderer.setAttribute(node, ARIA_EXPANDED_ATTR, 'true');
    }

    // Update UI classes to reflect current state
    this.updateClasses();
    
    // Apply alignment immediately without delay
    this.alignWrappersToRightIfStickOut();

    // Prevent event bubbling
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  /**
   * Navigate back in the menu hierarchy
   */
  back(): void {
    if (this.openNodes[this.openNodes.length - 1]) {
      this.renderer.removeClass(
        this.openNodes[this.openNodes.length - 1],
        'is-open'
      );
      
      if (this.featureConfigService?.isEnabled('a11yNavigationUiKeyboardControls')) {
        const removedNode = this.openNodes.pop();
        // Apply focus immediately without delay
        (removedNode?.querySelector('[tabindex="0"]') as HTMLElement)?.focus();
      } else {
        this.openNodes.pop();
      }
      
      this.updateClasses();
    }
  }

  /**
   * Reset navigation state by clearing all open nodes
   */
  clear(): void {
    this.openNodes = [];
    this.updateClasses();
  }

  /**
   * Updates CSS classes based on current navigation state
   */
  private updateClasses(): void {
    this.openNodes.forEach((node, i) => {
      if (i + 1 < this.openNodes.length) {
        this.renderer.addClass(node, 'is-opened');
        this.renderer.removeClass(node, 'is-open');
      } else {
        this.renderer.removeClass(node, 'is-opened');
        this.renderer.addClass(node, 'is-open');
      }
    });

    this.isOpen = this.openNodes.length > 0;
  }
  
  /**
   * Handles spacebar key events for accessibility navigation
   * @param event The UI event from spacebar press
   */
  onSpace(event: UIEvent): void {
    this.hamburgerMenuService.isExpanded
      .pipe(take(1))
      .subscribe((isExpanded: any) => {
        if (isExpanded) {
          // If menu is already expanded, toggle the current node
          this.toggleOpen(event);
          return;
        }
        if (!this.openNodes.length) {
          // If no nodes are open yet, toggle the current node
          this.toggleOpen(event);
          return;
        }
        event.preventDefault();
      });
      
    // Focus on the first focusable element in the node
    this.focusOnNode(event);
    
    // Set up keyboard arrow navigation
    this.setupArrowControls();
  }

  /**
   * Configures keyboard arrow navigation between menu items
   */
  setupArrowControls(): void {
    this.subscriptions.add(
      this.arrowControls.subscribe((e) => {
        e.preventDefault();
        const parentElement = (<HTMLElement>e.target).parentElement
          ?.parentElement;
        const nextLink = parentElement?.nextElementSibling?.querySelector('a');
        const previousLink =
          parentElement?.previousElementSibling?.querySelector('a');
        e.code === 'ArrowDown' ? nextLink?.focus() : previousLink?.focus();
      })
    );
  }

  /**
   * Focuses on the first interactive element in a node
   */
  focusOnNode(event: UIEvent): void {
    const firstFocusableElement =
      (<HTMLElement>event.target).nextElementSibling?.querySelector('button') ||
      (<HTMLElement>event.target).nextElementSibling?.querySelector('a');
    firstFocusableElement?.focus();
  }

  /**
   * Handles focus after a node was clicked
   */
  focusAfterPreviousClicked(event: MouseEvent) {
    const target: HTMLElement = <HTMLElement>(
      (event.target || event.relatedTarget)
    );
    if (
      target.ownerDocument.activeElement?.matches('nav[tabindex]') &&
      target.parentElement?.matches('.flyout')
    ) {
      target.focus();
    }
    return target.ownerDocument;
  }

  /**
   * Determines appropriate tab index for navigation elements
   */
  getTabIndex(node: NavigationNode, depth: number): 0 | -1 {
    if (!this.flyout) {
      return 0;
    }
    return depth > 0 && !node?.children ? -1 : 0;
  }
  
  /**
   * Handles mouse enter events for dropdown positioning
   */
  onMouseEnter(event: MouseEvent) {
    const target = <HTMLElement>event.currentTarget;
    this.alignWrapperToRightIfStickOut(target);
    this.focusAfterPreviousClicked(event);
    // Apply alignment immediately without delay
    this.alignWrappersToRightIfStickOut();
  }

  /**
   * Aligns a single dropdown wrapper to prevent it from extending beyond viewport
   * @param node The navigation node element containing the wrapper
   */
  private alignWrapperToRightIfStickOut(node: HTMLElement) {
    const wrapper = <HTMLElement>node.querySelector('.wrapper');
    if (!wrapper) return;
    
    // Reset any previously applied inline styles and classes
    this.renderer.removeStyle(wrapper, 'margin-left');
    this.renderer.removeStyle(wrapper, 'max-width');
    this.renderer.removeClass(wrapper, 'right-aligned');
    
    // Get viewport width and safety padding
    const viewportWidth = window.innerWidth;
    const padding = 16; // Buffer from edge of screen
    
    // Calculate positions
    const nodeRect = node.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
  
    
    // Handle standard LTR layout - if wider than viewport
    if (wrapperRect.width > viewportWidth - (padding * 2)) {
      this.renderer.setStyle(wrapper, 'max-width', `${viewportWidth - (padding * 2)}px`);
      this.renderer.setStyle(wrapper, 'margin-left', `-${nodeRect.left - padding}px`);
      return;
    }
    
    // If extending beyond right edge
    if (wrapperRect.right > viewportWidth - padding) {
      if (nodeRect.width > viewportWidth / 2) {
        // For large nodes, center the dropdown
        const centeredLeft = Math.max(padding, Math.round((viewportWidth - wrapperRect.width) / 2));
        this.renderer.setStyle(wrapper, 'margin-left', `${centeredLeft - nodeRect.left}px`);
      } else {
        // For smaller nodes, adjust to fit within viewport
        const marginLeft = -(wrapperRect.right - (viewportWidth - padding));
        this.renderer.setStyle(wrapper, 'margin-left', `${marginLeft}px`);
      }
    }
    
    // Ensure dropdown doesn't extend beyond left edge
    const currentMarginLeft = wrapper.style.marginLeft ? parseInt(wrapper.style.marginLeft, 10) : 0;
    if (wrapperRect.left + currentMarginLeft < padding) {
      this.renderer.setStyle(wrapper, 'margin-left', `${padding - wrapperRect.left}px`);
    }
  }

  /**
   * Aligns all dropdown wrappers within the component
   * to ensure they fit within the viewport
   */
  alignWrappersToRightIfStickOut(): void {
    if (!this.elemRef?.nativeElement) return;
    
    // Apply changes directly without requestAnimationFrame
    const wrappers = this.elemRef.nativeElement.querySelectorAll('.wrapper');
    const viewportWidth = window.innerWidth;
    const padding = 16; // Consistent padding with single node method
    
    wrappers.forEach((wrapper: HTMLElement) => {
      // Skip processing if wrapper is not visible
      if (!wrapper.offsetParent) return;
      
      // Reset previous positioning
      this.renderer.removeClass(wrapper, 'right-aligned');
      this.renderer.removeStyle(wrapper, 'margin-left');
      this.renderer.removeStyle(wrapper, 'max-width');
      this.renderer.removeStyle(wrapper, 'left');
      this.renderer.removeStyle(wrapper, 'right');
      
      const rect = wrapper.getBoundingClientRect();
      const parentEl = wrapper.parentElement;
      if (!parentEl) return;
      
      const parentRect = parentEl.getBoundingClientRect();
      
      // Standard LTR positioning logic
      // Case 1: Dropdown is wider than viewport
      if (rect.width > viewportWidth - (padding * 2)) {
        this.renderer.setStyle(wrapper, 'max-width', `${viewportWidth - (padding * 2)}px`);
        this.renderer.setStyle(wrapper, 'margin-left', `-${parentRect.left - padding}px`);
        return;
      }
      
      // Case 2: Dropdown extends beyond right edge
      if (rect.right > viewportWidth - padding) {
        // For large parent elements, center the dropdown
        if (parentRect.width > viewportWidth / 2) {
          const centeredLeft = Math.max(padding, Math.round((viewportWidth - rect.width) / 2));
          this.renderer.setStyle(wrapper, 'margin-left', `${centeredLeft - parentRect.left}px`);
        } else {
          // For regular sized parents, adjust to fit within viewport
          this.renderer.setStyle(wrapper, 'left', 'auto');
          this.renderer.setStyle(wrapper, 'right', '0');
          this.renderer.addClass(wrapper, 'right-aligned');
          
          // Ensure the dropdown isn't too wide
          if (rect.width > viewportWidth - 20) {
            this.renderer.setStyle(wrapper, 'max-width', `${viewportWidth - 20}px`);
          }
        }
      }
      
      // Case 3: Parent near left edge with dropdown extending beyond right edge
      if (parentRect.left < 10 && rect.right > viewportWidth - padding) {
        this.renderer.setStyle(wrapper, 'left', '10px');
        this.renderer.setStyle(wrapper, 'right', 'auto');
        this.renderer.setStyle(wrapper, 'max-width', `${viewportWidth - 20}px`);
      }
      
      // Case 4: Ensure dropdown doesn't extend beyond left edge
      const currentMarginLeft = wrapper.style.marginLeft ? parseInt(wrapper.style.marginLeft, 10) : 0;
      if (rect.left + currentMarginLeft < padding) {
        this.renderer.setStyle(wrapper, 'margin-left', `${padding - rect.left}px`);
      }
    });
  }

  /**
   * Adds event listeners for hovering over navigation items
   */
  private addHoverEventListeners(): void {
    const navItems = this.elemRef.nativeElement.querySelectorAll('li.nav-item');
    
    // Pre-calculate positions for all dropdown menus initially to avoid flicker
    this.alignWrappersToRightIfStickOut();
    
    // Setup event listeners for real-time adjustments
    const mouseEnterEvents = fromEvent(navItems, 'mouseenter');
    
    this.mouseEnterSubscription = mouseEnterEvents.subscribe((event) => {
      const mouseEvent = event as Event;
      const target = mouseEvent.target as HTMLElement;
      if (target) {
        // Apply alignment directly without requestAnimationFrame
        this.alignWrapperToRightIfStickOut(target);
      }
    });
  }

  /**
   * Calculates the depth of a navigation node
   */
  getTotalDepth(node: NavigationNode, depth = 0): number {
    if (node.children && node.children.length > 0) {
      return Math.max(
        ...node.children.map((n) => this.getTotalDepth(n, depth + 1))
      );
    } else {
      return depth;
    }
  }

  /**
   * Calculates number of columns for navigation layout
   */
  getColumnCount(length: number): number {
    return Math.round(length / (this.wrapAfter || length));
  }

  /**
   * Utility method to pass through values in templates
   */
  passTrought(value: any): any {
    return value;
  }
}